import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
} from "@mui/material";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  doc,
  getDoc,
  Timestamp,
} from "firebase/firestore";
import { TabContext, TabList, TabPanel } from "@mui/lab";

import { db, auth } from "../api/firebase";
import DisplayGroupEvents from "./DisplayGroupEvents";

const KomebuGroupEvents = ({ group, isMember }) => {
  const [value, setValue] = useState("1");
  const [events, setEvents] = useState([]);
  const [activeEvents, setActiveEvents] = useState([]);
  const [finishedEvents, setFinishedEvents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [isAdmin, setIsAdmin] = useState(false); // ★追加
  const [newEvent, setNewEvent] = useState({
    title: "",
    description: "",
    date: "",
  });

  const handleChange = (event, newValue) => setValue(newValue);

  const groupId = group?.id;

  // ★ admin 判定（users/{uid}.isAdmin）
  useEffect(() => {
    let cancelled = false;

    const fetchIsAdmin = async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) {
        if (!cancelled) setIsAdmin(false);
        return;
      }

      try {
        const userRef = doc(db, "users", uid);
        const snap = await getDoc(userRef);
        const admin = snap.exists() ? !!snap.data()?.isAdmin : false;
        if (!cancelled) setIsAdmin(admin);
      } catch (e) {
        console.error("isAdmin fetch error:", e);
        if (!cancelled) setIsAdmin(false);
      }
    };

    fetchIsAdmin();
    return () => {
      cancelled = true;
    };
  }, []);

  // ★ 作成権限
  const canCreate = useMemo(() => !!isMember || !!isAdmin, [isMember, isAdmin]);

  // グループのイベントを取得
  useEffect(() => {
    if (!groupId) return;

    const fetchEvents = async () => {
      const q = query(
        collection(db, "event"),
        where("target", "==", "group"),
        where("targetId", "==", groupId)
      );
      const eventDocs = await getDocs(q);
      const fetchedEvents = eventDocs.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setEvents(fetchedEvents);
    };

    fetchEvents();
  }, [groupId]);

  // 未開催イベントと終了イベントを分類（★dateが無い/型違いでも落ちないように防御）
  useEffect(() => {
    const now = new Date();
    const toJSDate = (v) => {
      if (!v) return null;
      if (typeof v?.toDate === "function") return v.toDate(); // Timestamp
      if (v instanceof Date) return v;
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? null : d;
    };

    const active = events.filter((e) => {
      const d = toJSDate(e.date);
      return d && d >= now;
    });
    const finished = events.filter((e) => {
      const d = toJSDate(e.date);
      return d && d < now;
    });

    setActiveEvents(active);
    setFinishedEvents(finished);
  }, [events]);

  const handleDialogOpen = () => setDialogOpen(true);
  const handleDialogClose = () => {
    setDialogOpen(false);
    setNewEvent({ title: "", description: "", date: "" });
  };

  const handleCreateEvent = async () => {
    if (!canCreate) {
      alert("イベント作成権限がありません");
      return;
    }
    if (!newEvent.title || !newEvent.date) {
      alert("タイトルと日付を入力してください");
      return;
    }

    try {
      const when = new Date(newEvent.date);
      if (Number.isNaN(when.getTime())) {
        alert("日付の形式が不正です");
        return;
      }

      await addDoc(collection(db, "event"), {
        title: newEvent.title,
        description: newEvent.description,
        date: Timestamp.fromDate(when), // ★ Timestampで統一（おすすめ）
        target: "group",
        targetId: groupId,
        createdAt: serverTimestamp(),
        creator: auth.currentUser?.uid || null,
      });

      alert("イベントが作成されました");
      handleDialogClose();
    } catch (err) {
      console.error("イベント作成エラー:", err);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* ★ isMember OR isAdmin で表示 */}
      {canCreate && (
        <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
          <Button variant="contained" color="primary" onClick={handleDialogOpen}>
            ＋イベント作成
          </Button>
        </Box>
      )}

      <TabContext value={value}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList onChange={handleChange} variant="scrollable" scrollButtons="auto">
            <Tab label="未開催イベント" value="1" />
            <Tab label="終了済イベント" value="2" />
          </TabList>
        </Box>

        <TabPanel value="1">
          <DisplayGroupEvents targetEvents={activeEvents} />
        </TabPanel>
        <TabPanel value="2">
          <DisplayGroupEvents targetEvents={finishedEvents} />
        </TabPanel>
      </TabContext>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>イベント作成</DialogTitle>
        <DialogContent>
          <TextField
            label="タイトル"
            fullWidth
            margin="normal"
            value={newEvent.title}
            onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
          />
          <TextField
            label="説明"
            fullWidth
            margin="normal"
            multiline
            rows={4}
            value={newEvent.description}
            onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
          />
          <TextField
            label="日付"
            type="datetime-local"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={newEvent.date}
            onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="secondary">
            キャンセル
          </Button>
          <Button onClick={handleCreateEvent} color="primary" variant="contained">
            作成
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KomebuGroupEvents;
